import React, { useState, useEffect, useRef } from 'react';
import { collection, query, getDocs, setDoc, doc, onSnapshot, writeBatch, getDoc } from 'firebase/firestore';
import { db, auth } from '../firebase';
import HeaderActionsPortal from './HeaderActionsPortal';
import { useT } from '../i18n.jsx';
import '../theme-palette.css';
import { Save, User, Calendar, RefreshCw, Upload, Download, Lock, Unlock } from 'lucide-react';
import { useOutletContext } from 'react-router-dom';
import Papa from 'papaparse';

const DEFAULT_DAYS = [
  { code: 'M', name: 'Monday' },
  { code: 'T', name: 'Tuesday' },
  { code: 'W', name: 'Wednesday' },
  { code: 'H', name: 'Thursday' },
  { code: 'F', name: 'Friday' }
];

const LessonAllocationAdmin = () => {
  const { t } = useT();
  const { activeSchoolId, schoolMetadata, isMobile } = useOutletContext();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isLocked, setIsLocked] = useState(true);

  const [teachers, setTeachers] = useState([]);
  const [profiles, setProfiles] = useState([]);
  const [academicYears, setAcademicYears] = useState([]);
  const [classes, setClasses] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [rooms, setRooms] = useState([]);

  const [selectedTeacher, setSelectedTeacher] = useState('');
  const [selectedProfileId, setSelectedProfileId] = useState('');
  const [selectedYearId, setSelectedYearId] = useState('');
  const [lessons, setLessons] = useState({}); // { 'M_1': { C: '', S: '', R: '' } } format: `${dayCode}_${periodIndex}`
  const fileInputRef = useRef(null);

  const [days, setDays] = useState(DEFAULT_DAYS);

  useEffect(() => {
    if (activeSchoolId) {
      fetchData();
    }
  }, [activeSchoolId]);

  const fetchData = async () => {
    setLoading(true);
    try {
      // 1. Fetch Teachers and their profiles for initials
      const sSnap = await getDocs(query(collection(db, 'schools', activeSchoolId, 'staff_profiles')));
      
      const tList = [];
      sSnap.forEach(d => {
        const prof = d.data();
        const role = (prof.role || prof.Role || '').toLowerCase();
        if (role === 'teacher' || role === 'tch' || role === 'admin') {
          const initial = prof.initial || d.id.split('@')[0].toUpperCase();
          const teacherName = prof.displayName || (prof.lastName ? `${prof.lastName || ''} ${prof.firstName || ''}`.trim() : d.id.split('@')[0]);
          const displayName = `${initial} - ${teacherName}`;
          tList.push({ uid: d.id, name: displayName, initial: initial });
        }
      });
      setTeachers(tList.sort((a, b) => a.initial.localeCompare(b.initial)));

      // 2. Fetch Timetable Profiles
      const pSnap = await getDocs(query(collection(db, 'schools', activeSchoolId, 'timetable_profiles')));
      const pList = [];
      pSnap.forEach(d => pList.push({ uid: d.id, ...d.data() }));
      setProfiles(pList);
      if (pList.length > 0) {
        const defProfile = pList.find(p => p.isDefault) || pList[0];
        setSelectedProfileId(defProfile.uid);
      }

      // 2.5 Fetch Academic Years
      const ySnap = await getDocs(query(collection(db, 'schools', activeSchoolId, 'academic_years')));
      const yList = [];
      ySnap.forEach(d => yList.push({ uid: d.id, ...d.data() }));
      yList.sort((a, b) => (a.yearId || '').localeCompare(b.yearId || ''));
      setAcademicYears(yList);
      if (yList.length > 0) {
        const currentYear = yList.find(y => y.cur_year) || yList[yList.length - 1];
        setSelectedYearId(currentYear.yearId);
      }

      // 3. System Parameters (Classes, Subjects, Rooms)
      const sysRef = doc(db, 'schools', activeSchoolId, 'metadata', 'system_settings');
      const sysSnap = await getDoc(sysRef);
      if (sysSnap.exists() && sysSnap.data().dropdowns) {
        const drop = sysSnap.data().dropdowns;
        setClasses(drop.classes || []);
        setSubjects(drop.subjects || []);
        setRooms(drop.locations || []);
      } else if (schoolMetadata?.dropdowns) {
        setClasses(schoolMetadata.dropdowns.classes || []);
        setSubjects(schoolMetadata.dropdowns.subjects || []);
        setRooms(schoolMetadata.dropdowns.locations || []);
      }
    } catch (e) {
      console.error(e);
    }
    setLoading(false);
  };

  useEffect(() => {
    if (activeSchoolId && selectedTeacher && selectedYearId) {
      const unsub = onSnapshot(collection(db, 'schools', activeSchoolId, 'teacher_lessons'), (snap) => {
        const teacherLessons = {};
        snap.forEach(d => {
          const data = d.data();
          if (data.T === selectedTeacher && data.Y === selectedYearId) {
            teacherLessons[data.P] = { C: data.C || '', S: data.S || '', R: data.R || '' };
          }
        });
        setLessons(teacherLessons);
      });
      return () => unsub();
    } else {
      setLessons({});
    }
  }, [activeSchoolId, selectedTeacher, selectedYearId]);

  const handleLessonChange = (periodCode, field, value) => {
    setLessons(prev => {
      const existing = prev[periodCode] || { C: '', S: '', R: '' };
      return {
        ...prev,
        [periodCode]: { ...existing, [field]: value }
      };
    });
  };

  const handleSaveLessons = async () => {
    if (!selectedTeacher || !selectedYearId) return;
    setSaving(true);
    try {
      const batch = writeBatch(db);
      
      // We need to delete existing lessons for this teacher in this academic year and add new ones
      const existingSnap = await getDocs(query(collection(db, 'schools', activeSchoolId, 'teacher_lessons')));
      existingSnap.forEach(d => {
        const data = d.data();
        if (data.T === selectedTeacher && data.Y === selectedYearId) {
          batch.delete(doc(db, 'schools', activeSchoolId, 'teacher_lessons', d.id));
        }
      });

      // Add new lessons
      Object.keys(lessons).forEach(pCode => {
        const l = lessons[pCode];
        if (l.C || l.S || l.R) { // Only save if at least one field is filled
          const newRef = doc(db, 'schools', activeSchoolId, 'teacher_lessons', `${selectedYearId}_${selectedTeacher}_${pCode}`);
          batch.set(newRef, {
            Y: selectedYearId,
            T: selectedTeacher,
            P: pCode,
            C: l.C,
            S: l.S,
            R: l.R
          });
        }
      });

      await batch.commit();
      alert(t('lessonAllocation.saved'));
    } catch (e) {
      alert(t('lessonAllocation.saveFailed', { message: e.message }));
    }
    setSaving(false);
  };

  const handleExportCSV = async () => {
    if (!selectedYearId) {
      alert(t('lessonAllocation.pickYearToExport'));
      return;
    }
    try {
      const snap = await getDocs(query(collection(db, 'schools', activeSchoolId, 'teacher_lessons')));
      const data = [];
      snap.forEach(d => {
        const item = d.data();
        if (item.Y === selectedYearId) {
          data.push({
            'Key': `${item.Y}_${item.T}_${item.P}`,
            'School Year': item.Y || '',
            'Teacher Initial': item.T || '',
            'Period Code': item.P || '',
            'Class': item.C || '',
            'Subject': item.S || '',
            'Room': item.R || ''
          });
        }
      });
      
      const csv = Papa.unparse(data);
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.setAttribute('href', url);
      link.setAttribute('download', `teacher_timetable_${selectedYearId}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (e) {
      alert(t('lessonAllocation.exportFailed', { message: e.message }));
    }
  };

  const handleImportCSV = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: async (results) => {
        if (results.errors.length > 0) {
          alert(t('lessonAllocation.parseFailed'));
          return;
        }

        const data = results.data;
        if (!data || data.length === 0) {
          alert(t('lessonAllocation.emptyCsv'));
          return;
        }

        setSaving(true);
        try {
          let count = 0;
          let currentBatch = writeBatch(db);
          
          for (const row of data) {
            const initial = String(row['Teacher Initial'] || '').trim();
            const p = String(row['Period Code'] || '').trim();
            const y = String(row['School Year'] || selectedYearId).trim();
            const providedKey = String(row['Key'] || '').trim();
            
            if (!initial || !p || !y) continue;

            const c = String(row['Class'] || '').trim();
            const s = String(row['Subject'] || '').trim();
            const r = String(row['Room'] || '').trim();

            const docId = providedKey || `${y}_${initial}_${p}`;
            const newRef = doc(db, 'schools', activeSchoolId, 'teacher_lessons', docId);
            currentBatch.set(newRef, {
              Y: y,
              T: initial,
              P: p,
              C: c,
              S: s,
              R: r
            });
            
            count++;
            if (count % 400 === 0) {
              await currentBatch.commit();
              currentBatch = writeBatch(db);
            }
          }
          if (count % 400 !== 0) {
            await currentBatch.commit();
          }

          alert(t('lessonAllocation.imported', { count }));
          if (selectedTeacher) {
            // trigger re-fetch indirectly if needed, or just let snapshot handle it
          }
        } catch (error) {
          console.error(error);
          alert(t('lessonAllocation.importFailed', { message: error.message }));
        }
        setSaving(false);
        e.target.value = null;
      }
    });
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <RefreshCw className="animate-spin" size={24} color="var(--color-text-muted)" />
      </div>
    );
  }

  const currentProfile = profiles.find(p => p.uid === selectedProfileId);
  const periods = currentProfile?.periods || [];

  return (
    <div className="animate-fade-in new-theme" style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
      
      {/* Top Bar Actions */}
      <div className="theme-tabs-bar">
        <div className="theme-tabs-group">
          {[
            { id: 'teacher', label: t('lessonAllocation.tab'), icon: Calendar }
          ].map(tab => {
            const Icon = tab.icon;
            const isActive = true;
            return (
              <button
                key={tab.id}
                className={`theme-tab ${isActive ? 'active' : ''}`}
              >
                <div className="theme-tab-icon">
                  <Icon size={20} />
                </div>
                {tab.label}
              </button>
            );
          })}
        </div>
        
        <HeaderActionsPortal>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
          <select
            className="input-field"
            value={selectedYearId}
            onChange={e => setSelectedYearId(e.target.value)}
            style={{ width: '130px', margin: 0, padding: '0.4rem 0.5rem', height: 'auto', fontSize: '0.875rem' }}
          >
            {academicYears.length === 0 && <option value="">{t('lessonAllocation.noYear')}</option>}
            {academicYears.map(y => (
              <option key={y.uid} value={y.yearId}>{y.yearId}</option>
            ))}
          </select>

          <select
            className="input-field"
            value={selectedTeacher}
            onChange={e => setSelectedTeacher(e.target.value)}
            style={{ width: '180px', margin: 0, padding: '0.4rem 0.5rem', height: 'auto', fontSize: '0.875rem' }}
          >
            <option value="">{t('lessonAllocation.chooseTeacher')}</option>
            {teachers.map(teacher => (
              <option key={teacher.uid} value={teacher.initial}>{teacher.name}</option>
            ))}
          </select>

          <select
            className="input-field"
            value={selectedProfileId}
            onChange={e => setSelectedProfileId(e.target.value)}
            style={{ width: '180px', margin: 0, padding: '0.4rem 0.5rem', height: 'auto', fontSize: '0.875rem' }}
          >
            {profiles.map(p => (
              <option key={p.uid} value={p.uid}>{p.name} {p.isDefault ? t('lessonAllocation.defaultSuffix') : ''}</option>
            ))}
          </select>

          <div style={{ width: '1px', height: '24px', backgroundColor: 'var(--color-border)', margin: '0 0.5rem' }} />

          <input
            type="file"
            accept=".csv"
            style={{ display: 'none' }}
            ref={fileInputRef}
            onChange={handleImportCSV}
          />
          <button onClick={() => fileInputRef.current?.click()} disabled={saving || isLocked} title={t('lessonAllocation.importCsv')} className="btn-secondary" style={{ width: 'auto', padding: '0.4rem 0.75rem', fontSize: '0.875rem', whiteSpace: 'nowrap' }}>
            <Upload size={16} />
          </button>
          <button onClick={handleExportCSV} disabled={saving} title={t('lessonAllocation.exportCsv')} className="btn-secondary" style={{ width: 'auto', padding: '0.4rem 0.75rem', fontSize: '0.875rem', whiteSpace: 'nowrap' }}>
            <Download size={16} />
          </button>
          <button onClick={() => setIsLocked(!isLocked)} className="btn-secondary" style={{ width: 'auto', padding: '0.4rem 0.75rem', fontSize: '0.875rem', whiteSpace: 'nowrap', color: isLocked ? 'var(--color-primary)' : 'var(--color-danger)', borderColor: isLocked ? 'var(--color-primary)' : 'var(--color-danger)' }}>
            {isLocked ? <><Lock size={16} /> Unlock to Edit</> : <><Unlock size={16} /> Lock Grid</>}
          </button>
          <button onClick={handleSaveLessons} disabled={!selectedTeacher || saving || isLocked} className="btn-primary" style={{ width: 'auto', padding: '0.4rem 0.75rem', fontSize: '0.875rem', whiteSpace: 'nowrap' }}>
            <Save size={16} /> {saving ? t('common.saving') : t('lessonAllocation.saveTimetable')}
          </button>
        </div>
        </HeaderActionsPortal>
      </div>

      {selectedTeacher ? (
        <div style={{ flex: 1, backgroundColor: 'var(--color-bg-card)', borderRadius: isMobile ? 0 : 'var(--radius-lg)', boxShadow: isMobile ? 'none' : 'var(--shadow-sm)', overflow: 'auto', border: isMobile ? 'none' : '1px solid var(--color-border)', position: 'relative' }}>
          {isLocked && (
            <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(255, 255, 255, 0.4)', zIndex: 1, pointerEvents: 'none' }} />
          )}
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '1000px' }}>
            <thead>
              <tr style={{ backgroundColor: 'var(--color-bg-app)' }}>
                <th style={{ padding: '0.75rem', borderBottom: '2px solid var(--color-border)', borderRight: '1px solid var(--color-border)', width: '150px', position: 'sticky', left: 0, backgroundColor: 'var(--color-bg-app)', zIndex: 10 }}>{t('lessonAllocation.period')}</th>
                {days.map(d => (
                  <th key={d.code} style={{ padding: '0.75rem', borderBottom: '2px solid var(--color-border)', borderRight: '1px solid var(--color-border)', textAlign: 'center', fontWeight: '600', color: 'var(--color-text-main)' }}>
                    {d.code}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {periods.map((period, pIndex) => (
                <tr key={pIndex}>
                  <td style={{ padding: '0.5rem 0.75rem', borderBottom: '1px solid var(--color-border)', borderRight: '1px solid var(--color-border)', backgroundColor: (period.type === 'Recess' || period.type === 'Lunch') ? 'var(--color-success-bg)' : 'var(--color-bg-subtle)', position: 'sticky', left: 0, zIndex: 5 }}>
                    <div style={{ fontWeight: '600', color: 'var(--color-text-main)' }}>{period.name}</div>
                    <div style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)' }}>{period.startTime} - {period.endTime}</div>
                  </td>
                  {days.map(d => {
                    let periodSlot;
                    if (period.type === 'Lesson') {
                      periodSlot = period.name.replace(/\D/g, '') || pIndex;
                    } else {
                      periodSlot = pIndex === 0 ? '0' : `${period.type.charAt(0).toUpperCase()}${period.name.replace(/\D/g, '') || pIndex}`;
                    }
                    const pCode = `${d.code}${periodSlot}`;
                    const lesson = lessons[pCode] || { C: '', S: '', R: '' };

                    return (
                      <td key={d.code} style={{ padding: '0.25rem', borderBottom: '1px solid var(--color-border)', borderRight: '1px solid var(--color-border)', verticalAlign: 'top', position: 'relative', backgroundColor: (period.type === 'Recess' || period.type === 'Lunch') ? 'var(--color-success-bg-soft)' : 'transparent' }}>
                        {period.type !== 'Lesson' && (
                          <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, display: 'flex', justifyContent: 'center', alignItems: 'center', color: 'var(--color-text-muted)', fontSize: '0.75rem', fontStyle: 'italic', opacity: 0.15, pointerEvents: 'none', zIndex: 1 }}>
                            {period.type}
                          </div>
                        )}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', position: 'relative', zIndex: 2 }}>
                          {isLocked ? (
                            (lesson.C || lesson.S || lesson.R) ? (
                              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.15rem', padding: '0.15rem' }}>
                                <div style={{ fontSize: '0.85rem', fontWeight: '600', color: 'var(--color-primary)' }}>{lesson.C || '-'}</div>
                                <div style={{ fontSize: '0.8rem', color: 'var(--color-text-main)' }}>{lesson.S || '-'}</div>
                                <div style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)' }}>{lesson.R || '-'}</div>
                              </div>
                            ) : null
                          ) : (
                            <>
                              <select 
                                value={lesson.C} 
                                onChange={e => handleLessonChange(pCode, 'C', e.target.value)}
                                disabled={isLocked}
                                style={{ textAlign: 'center', width: '100%', padding: '0.25rem', fontSize: '0.8rem', borderRadius: '4px', border: '1px solid var(--color-border)', backgroundColor: lesson.C ? 'var(--color-info-bg)' : 'rgba(255,255,255,0.7)', cursor: isLocked ? 'not-allowed' : 'default' }}
                              >
                                <option value="">{t('lessonAllocation.className')}</option>
                                {classes.filter(c => c.active !== false).map(c => <option key={c.code} value={c.code}>{c.nameEn || c.code}</option>)}
                              </select>
                              
                              <select 
                                value={lesson.S} 
                                onChange={e => handleLessonChange(pCode, 'S', e.target.value)}
                                disabled={isLocked}
                                style={{ textAlign: 'center', width: '100%', padding: '0.25rem', fontSize: '0.8rem', borderRadius: '4px', border: '1px solid var(--color-border)', backgroundColor: lesson.S ? 'var(--color-success-bg)' : 'rgba(255,255,255,0.7)', cursor: isLocked ? 'not-allowed' : 'default' }}
                              >
                                <option value="">{t('lessonAllocation.subject')}</option>
                                {subjects.filter(s => s.active !== false).map(s => <option key={s.code} value={s.code}>{s.code}</option>)}
                              </select>

                              <select 
                                value={lesson.R} 
                                onChange={e => handleLessonChange(pCode, 'R', e.target.value)}
                                disabled={isLocked}
                                style={{ textAlign: 'center', width: '100%', padding: '0.25rem', fontSize: '0.8rem', borderRadius: '4px', border: '1px solid var(--color-border)', backgroundColor: lesson.R ? 'var(--color-warning-bg)' : 'rgba(255,255,255,0.7)', cursor: isLocked ? 'not-allowed' : 'default' }}
                              >
                                <option value="">{t('lessonAllocation.room')}</option>
                                {rooms.filter(r => r.active !== false).map(r => <option key={r.code} value={r.code}>{r.code}</option>)}
                              </select>
                            </>
                          )}
                        </div>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', color: 'var(--color-text-muted)', backgroundColor: 'var(--color-bg-card)', borderRadius: 'var(--radius-lg)', border: '1px dashed var(--color-border)' }}>
          <User size={48} style={{ opacity: 0.2, marginBottom: '1rem' }} />
          <p>{t('lessonAllocation.pickTeacher')}</p>
        </div>
      )}
    </div>
  );
};

export default LessonAllocationAdmin;
